/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useCallback, useEffect } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import LabelledField from "components/labelled-field";
import { Typography, IconButton, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArrowCircleDown, ArrowCircleUp } from "@mui/icons-material";
import { ArrowDropDownCircle } from "@material-ui/icons";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import { generateGuid } from "utils/empty-exists";
import { debounce } from "lodash";
import isEqual from "react-fast-compare";
import { Logger } from "cms/utils/log/logger";

const AccordionSectionEdit = (props) => {

    const {
        section: _section,
        updateSection,
        index,
        deleteSection,
        moveSection,
        sectionCount,
        expanded,
        handleExpandChange,
    } = props || {};

    const {
        title: _title,
        richtext: _richtext
    } = _section || {};

    const [section, setSection] = useState(_section);
    const [title, setTitle] = useState(_title); // text
    const [richtext, setRichtext] = useState(_richtext);
    const tabId = _section.tab_id ? _section.tab_id : generateGuid(); // need any unique id, so generate if missing
    const identifier = `section-${section.tab_id}`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((nextSection) => {
            if (!isEqual(section, nextSection)) {
                Logger.debug("updating", nextSection);
                setSection(nextSection);
                updateSection(index, nextSection);
            }
        }, 500),
        [updateSection]
    );

    useEffect(
        () => {
            const nextSection = { ...section, title, richtext };
            update(nextSection);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [title, richtext]
    );

    const onChangeTitle = async (event) => {
        const newTitle = event.target.value;
        setTitle(newTitle);
    };
    const onChangeRichText = async (newRichText) => {
        setRichtext(newRichText);
    };

    const onDeleteSection = (event) => {
        event.stopPropagation();
        deleteSection(index);
    };
    const onMoveUp = (event) => {
        event.stopPropagation();
        moveSection(index, "UP");
    };
    const onMoveDown = (event) => {
        event.stopPropagation();
        moveSection(index, "DOWN");
    };

    return (
        <Fragment>
            {section &&
                <Accordion
                    expanded={expanded === identifier}
                    onChange={handleExpandChange(identifier)}
                    className='accordion-component-header'
                >
                    {/*     SECTION HEADER     */}
                    <AccordionSummary
                        expandIcon={<ArrowDropDownCircle />}
                        aria-controls={`${identifier}-content`}
                        id={`${identifier}-header`}
                        className='accordion-component-summary'
                    >

                        {/*  Header  */}
                        <div className='accordion-component-summary-inner'>
                            <div className='accordion-component-summary-content'>
                                {expanded !== identifier && (
                                    <Typography variant='h5' color={title ? "text.primary" : "text.disabled"}>
                                        {title || <div className='accordion-component-section-edit-prompt'>Click here to edit section</div>}
                                    </Typography>
                                )}
                                {expanded === identifier && (
                                    <TextField
                                        id={`${tabId}-section-title`}
                                        onClick={(event) => {
                                            event.stopPropagation(); 
                                        }}
                                        placeholder={"Enter tile for section"}
                                        fullWidth
                                        defaultValue={title}
                                        onChange={onChangeTitle}
                                        className='accordion-component-summary-content-input'
                                    />
                                )}
                            </div>

                            {/*  Buttons  */}
                            <div>
                                <IconButton
                                    aria-label={"delete"}
                                    onClick={onDeleteSection}
                                ><DeleteIcon /></IconButton>
                                <IconButton
                                    aria-label={"move down"}
                                    disabled={index === sectionCount - 1}
                                    onClick={onMoveDown}
                                ><ArrowCircleDown />
                                </IconButton>
                                <IconButton
                                    aria-label={"move up"}
                                    disabled={index === 0}
                                    onClick={onMoveUp}
                                ><ArrowCircleUp /></IconButton>
                            </div>

                        </div>
                    </AccordionSummary>

                    {/*  Accordion Content  */}
                    <AccordionDetails>
                        <div className='accordion-component-details-panel'>

                            {/*     Rich Text     */}
                            <LabelledField label='Content'>
                                <SimplicityComposerEnsureTipTap
                                    richtext={richtext}
                                    onChange={onChangeRichText}
                                />
                            </LabelledField>

                        </div>
                    </AccordionDetails>
                </Accordion>
            }
        </Fragment>
    );
};
AccordionSectionEdit.displayName = "AccordionSectionEdit";
export default AccordionSectionEdit;
